import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canCreate, canDelete, canRead, canUpdate } from '../../util/access';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	FormControlLabel,
	Grid,
	IconButton,
	ImageList,
	ImageListItem,
	ImageListItemBar,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	Add,
	Close,
	CloudUpload,
	Delete,
	DeleteOutline,
	Fullscreen,
	ImageOutlined,
	Inventory2,
	PhotoCamera,
	Save,
} from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
	ItemImageRole,
	putItemImageBlob,
	resolveItemImageUrl,
	resolveItemImageUrlFromCache,
} from "../../util/itemImageCache";
import { compressImageForUpload } from "../../util/compressImageForUpload";
import { translateProductMasterError } from "../../util/productMasterErrors";
import { Util } from "../../util/Util";
import { ITEM_NAME_MAX_LENGTH, MONEY_LIMITS, QTY_LIMITS, parseQtyInput, qtyStepAttr, roundMoney } from "../../util/quantity";

export const IMAGE_ROLES: ItemImageRole[] = [
	"main",
	"front",
	"back",
	"left",
	"right",
	"top",
	"bottom",
	"detail-1",
	"detail-2",
	"detail-3",
	"lifestyle-1",
	"lifestyle-2",
	"packaging",
];

export type CatalogItemRow = {
	id: number;
	ItemCode?: string;
	ItemName?: string;
	Category?: string;
	Family?: string;
	Sector?: string;
	ItemCatId?: number;
	FamilyId?: number;
	SectorId?: number;
	SellingPrice?: number;
	CostPrice?: number;
	MinSellPrice?: number;
	MinOrderQty?: number;
	IsFractional?: boolean;
	IsMetered?: boolean;
	IsActive?: boolean;
	Created?: string;
	Updated?: string;
	/** Barcodes attached to this product (from master API) */
	barcodes?: string[];
};

type LookupOpt = { id: number; label: string };

type FormState = {
	item_code: string;
	item_name: string;
	item_cat: number;
	family: number;
	sector: number;
	cost_price: number;
	selling_price: number;
	min_sell_price: number;
	min_order_qty: number;
	is_fractional: boolean;
	is_metered: boolean;
	is_active: boolean;
};

interface Props {
	isOpen: boolean;
	toggle: () => void;
	/** Existing product, or null when registering a new one */
	item: CatalogItemRow | null;
	/** Open in create mode (new product) */
	createMode?: boolean;
	/** When master data saved or product created successfully */
	onItemSaved?: (item: CatalogItemRow, created?: boolean) => void;
	/** When product was deleted */
	onItemDeleted?: (itemId: number) => void;
	/** When an image role was uploaded (cache already updated) */
	onImageUpdated?: (itemId: number, role: string) => void | Promise<void>;
}

const emptyForm = (defaults?: Partial<FormState>): FormState => ({
	item_code: "",
	item_name: "",
	item_cat: defaults?.item_cat ?? 1,
	family: defaults?.family ?? 1,
	sector: defaults?.sector ?? 1,
	cost_price: 0,
	selling_price: 0,
	min_sell_price: 0,
	min_order_qty: 1,
	is_fractional: false,
	is_metered: true,
	is_active: true,
});

/**
 * Product master detail: cache-backed image gallery + editable item fields.
 * Supports create (register) and edit. Images after the product has an id.
 */
const ItemDetailModal = ({
	isOpen,
	toggle,
	item,
	createMode = false,
	onItemSaved,
	onItemDeleted,
	onImageUpdated,
}: Props) => {
	const { t } = useTranslation("common");
	const { api, access } = useAuth();
	const themeColor = useMoleThemeColor();

	const [availableImages, setAvailableImages] = useState<{ role: string; url: string }[]>([]);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [probing, setProbing] = useState(false);
	const [fullscreenOpen, setFullscreenOpen] = useState(false);
	const [uploadingRole, setUploadingRole] = useState<string | null>(null);
	const [imageBust, setImageBust] = useState(0);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	// The column layer. Photos are a capability of the item resource, not a
	// column, so they have their own rule; the backend refuses either way.
	const canEditImages = canCreate(access, "item", "images") || canUpdate(access, "item", "images");
	const canSeeCost = canRead(access, "item", "cost_price");
	const canSetCost = canCreate(access, "item", "cost_price") && canUpdate(access, "item", "cost_price");
	const canRemoveItem = canDelete(access, "item");
	const [form, setForm] = useState<FormState | null>(null);
	const [dirty, setDirty] = useState(false);
	/** Local working item — set after create so gallery can unlock without remounting parent */
	const [workingItem, setWorkingItem] = useState<CatalogItemRow | null>(null);
	/** Desired barcode set (add/remove; saved with product master) */
	const [barcodes, setBarcodes] = useState<string[]>([]);
	const [barcodeDraft, setBarcodeDraft] = useState("");

	const [categories, setCategories] = useState<LookupOpt[]>([]);
	const [families, setFamilies] = useState<LookupOpt[]>([]);
	const [sectors, setSectors] = useState<LookupOpt[]>([]);
	const [lookupsLoading, setLookupsLoading] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);
	const pendingRoleRef = useRef<string>("main");

	const isCreate = createMode && !(workingItem?.id || item?.id);
	const activeItem = workingItem || item;
	const itemId = activeItem?.id ?? 0;

	const buildForm = useCallback((row: CatalogItemRow): FormState => ({
		item_code: row.ItemCode || "",
		item_name: row.ItemName || "",
		item_cat: Number(row.ItemCatId) || 1,
		family: Number(row.FamilyId) || 1,
		sector: Number(row.SectorId) || 1,
		cost_price: Number(row.CostPrice) || 0,
		selling_price: Number(row.SellingPrice) || 0,
		min_sell_price: Number(row.MinSellPrice) || 0,
		min_order_qty: Number(row.MinOrderQty) || 1,
		is_fractional: row.IsFractional === true,
		is_metered: row.IsMetered !== false,
		is_active: row.IsActive !== false,
	}), []);

	// Load lookup tables once when modal opens
	useEffect(() => {
		if (!isOpen) return;
		let cancelled = false;
		const load = async () => {
			setLookupsLoading(true);
			try {
				const [catRes, famRes, secRes] = await Promise.all([
					api.get("item_cat"),
					api.get("family"),
					api.get("sector"),
				]);
				if (cancelled) return;
				if (catRes?.status === 200 && Array.isArray(catRes.data)) {
					setCategories(
						catRes.data
							.map((c: any) => ({ id: c.id, label: c.cat_name || String(c.id) }))
							.sort((a: LookupOpt, b: LookupOpt) => a.label.localeCompare(b.label)),
					);
				}
				if (famRes?.status === 200 && Array.isArray(famRes.data)) {
					setFamilies(
						famRes.data
							.map((f: any) => ({ id: f.id, label: f.fam_name || String(f.id) }))
							.sort((a: LookupOpt, b: LookupOpt) => a.label.localeCompare(b.label)),
					);
				}
				if (secRes?.status === 200 && Array.isArray(secRes.data)) {
					setSectors(
						secRes.data
							.map((s: any) => ({ id: s.id, label: s.sec_name || String(s.id) }))
							.sort((a: LookupOpt, b: LookupOpt) => a.label.localeCompare(b.label)),
					);
				}
			} finally {
				if (!cancelled) setLookupsLoading(false);
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
	}, [isOpen, api]);

	// Reset form when opening / item changes (do not depend on lookups — avoids wiping typing)
	useEffect(() => {
		if (!isOpen) {
			setForm(null);
			setDirty(false);
			setWorkingItem(null);
			setAvailableImages([]);
			setBarcodes([]);
			setBarcodeDraft("");
			return;
		}
		if (createMode && !item) {
			setWorkingItem(null);
			setForm(emptyForm());
			setBarcodes([]);
			setBarcodeDraft("");
			setDirty(true);
			setAvailableImages([]);
			setProbing(false);
			return;
		}
		if (item) {
			setWorkingItem(null);
			setForm(buildForm(item));
			setBarcodes([...(item.barcodes || [])]);
			setBarcodeDraft("");
			setDirty(false);
			// Load full master (incl. barcodes) when opening existing product
			if (item.id > 0) {
				void (async () => {
					try {
						const res = await api.get(`items/master/${item.id}`);
						if (res?.status === 200 && res.data?.item) {
							const full = res.data.item as CatalogItemRow;
							setForm(buildForm(full));
							setBarcodes([...(full.barcodes || [])]);
							setWorkingItem(full);
						}
					} catch {
						// keep list-row data
					}
				})();
			}
		}
	}, [isOpen, item, createMode, buildForm, api]);

	// Apply first lookup options as defaults once after create open
	const createDefaultsApplied = useRef(false);
	useEffect(() => {
		if (!isOpen || !createMode || item) {
			createDefaultsApplied.current = false;
			return;
		}
		if (createDefaultsApplied.current) return;
		if (!categories.length && !families.length && !sectors.length) return;
		createDefaultsApplied.current = true;
		setForm((prev) =>
			prev
				? {
						...prev,
						item_cat: categories[0]?.id ?? prev.item_cat,
						family: families[0]?.id ?? prev.family,
						sector: sectors[0]?.id ?? prev.sector,
					}
				: prev,
		);
	}, [isOpen, createMode, item, categories, families, sectors]);

	/**
	 * Gallery: prefer IDB/memory (same store as POS + sync worker).
	 * Only network-fetch missing roles once via resolveItemImageUrl (fills cache for next time).
	 * Do not use raw static URL probes — those miss cache and often 404 non-jpg paths.
	 */
	const loadGalleryFromCache = useCallback(async () => {
		if (!itemId) return;
		setProbing(true);
		setSelectedIndex(0);
		setAvailableImages([]);

		const found: { role: string; url: string }[] = [];
		for (const role of IMAGE_ROLES) {
			try {
				let url = await resolveItemImageUrlFromCache(itemId, role);
				// Warm main (and any role already requested) from network if not cached yet
				if (!url && (role === "main" || role === "front")) {
					const resolved = await resolveItemImageUrl(itemId, role, api);
					url = resolved.url;
				}
				if (url) found.push({ role, url: `${url}#${imageBust}` });
			} catch {
				// skip
			}
		}
		setAvailableImages(found);
		setProbing(false);
	}, [itemId, api, imageBust]);

	useEffect(() => {
		if (!isOpen || !itemId) return;
		void loadGalleryFromCache();
	}, [isOpen, itemId, loadGalleryFromCache]);

	useEffect(() => {
		if (!isOpen || availableImages.length === 0) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "ArrowRight" || e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((p) => (p + 1) % availableImages.length);
			} else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((p) => (p - 1 + availableImages.length) % availableImages.length);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [isOpen, availableImages.length]);

	const openFilePicker = (role: string) => {
		pendingRoleRef.current = role;
		fileInputRef.current?.click();
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file || !itemId) return;
		const role = pendingRoleRef.current || "main";
		setUploadingRole(role);
		try {
			// Downscale heavy phone photos before upload (max edge 1280, JPEG ~0.82)
			const optimized = await compressImageForUpload(file);
			const formData = new FormData();
			formData.append("image", optimized);
			formData.append("role", role);
			const res = await api.post(`items/${itemId}/image`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			if (res && (res.status === 200 || res.status === 201)) {
				toast.success(t("productCatalog.uploadSuccess", { defaultValue: "Image updated" }));
				await putItemImageBlob(itemId, role, optimized);
				setImageBust(Date.now());
				await onImageUpdated?.(itemId, role);
				// Refresh gallery list from cache
				const { url } = await resolveItemImageUrl(itemId, role, api);
				if (url) {
					setAvailableImages((prev) => {
						const next = [...prev.filter((i) => i.role !== role), { role, url: `${url}#${Date.now()}` }];
						next.sort((a, b) => IMAGE_ROLES.indexOf(a.role) - IMAGE_ROLES.indexOf(b.role));
						return next;
					});
					setSelectedIndex((prev) => {
						const next = [...availableImages.filter((i) => i.role !== role), { role, url: "" }];
						next.sort((a, b) => IMAGE_ROLES.indexOf(a.role) - IMAGE_ROLES.indexOf(b.role));
						const idx = next.findIndex((i) => i.role === role);
						return idx >= 0 ? idx : prev;
					});
				}
			} else {
				toast.error(t("productCatalog.uploadFailed", { defaultValue: "Image upload failed" }));
			}
		} catch {
			toast.error(t("productCatalog.uploadFailed", { defaultValue: "Image upload failed" }));
		} finally {
			setUploadingRole(null);
		}
	};

	const patchForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
		setDirty(true);
	};

	const addBarcode = () => {
		const code = barcodeDraft.trim();
		if (!code) return;
		const exists = barcodes.some((b) => Util.compareStrings(b, code));
		if (exists) {
			toast.warn(t("productCatalog.barcodeDuplicate", { defaultValue: "Barcode already in the list" }));
			return;
		}
		setBarcodes((prev) => [...prev, code]);
		setBarcodeDraft("");
		setDirty(true);
	};

	const removeBarcode = (code: string) => {
		setBarcodes((prev) => prev.filter((b) => !Util.compareStrings(b, code)));
		setDirty(true);
	};

	const applyMasterResponse = (full: CatalogItemRow, created: boolean) => {
		setWorkingItem(full);
		setForm(buildForm(full));
		setBarcodes([...(full.barcodes || [])]);
		setDirty(false);
		onItemSaved?.(full, created);
	};

	const handleSave = async () => {
		if (!form) return;
		if (!form.item_name.trim()) {
			toast.error(t("productCatalog.nameRequired"));
			return;
		}

		// Include typed barcode not yet added via the Add button
		let barcodesToSave = [...barcodes];
		const pending = barcodeDraft.trim();
		if (pending && !barcodesToSave.some((b) => Util.compareStrings(b, pending))) {
			barcodesToSave = [...barcodesToSave, pending];
			setBarcodes(barcodesToSave);
			setBarcodeDraft("");
		}

		setSaving(true);
		try {
			const code = form.item_code.trim();
			const body: Record<string, unknown> = {
				item_name: form.item_name.trim(),
				item_cat: form.item_cat,
				family: form.family,
				sector: form.sector,
				cost_price: form.cost_price,
				selling_price: form.selling_price,
				min_sell_price: form.min_sell_price,
				min_order_qty: form.min_order_qty,
				is_fractional: form.is_fractional,
				is_metered: form.is_metered,
				is_active: form.is_active,
				barcodes: barcodesToSave,
				item_code: code || null,
			};

			if (isCreate || !itemId) {
				const res = await api.post("items/master", body);
				if (res && (res.status === 200 || res.status === 201) && res.data?.item) {
					const full = res.data.item as CatalogItemRow;
					applyMasterResponse(full, true);
					toast.success(t("productCatalog.createSuccessImages"));
				} else {
					toast.error(
						translateProductMasterError(t, res, "productCatalog.createFailed"),
					);
				}
			} else {
				const res = await api.put(`items/master/${itemId}`, body);
				if (res && (res.status === 200 || res.status === 201) && res.data?.item) {
					const full = res.data.item as CatalogItemRow;
					applyMasterResponse(full, false);
					toast.success(t("productCatalog.saveSuccess"));
				} else {
					toast.error(
						translateProductMasterError(t, res, "productCatalog.saveFailed"),
					);
				}
			}
		} catch (err: unknown) {
			toast.error(
				translateProductMasterError(
					t,
					err,
					isCreate ? "productCatalog.createFailed" : "productCatalog.saveFailed",
				),
			);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!itemId) return;
		setDeleting(true);
		try {
			const res = await api.delete(`items/master/${itemId}`);
			if (res && (res.status === 200 || res.status === 204)) {
				toast.success(t("productCatalog.deleteSuccess"));
				onItemDeleted?.(itemId);
				toggle();
			} else {
				toast.error(
					translateProductMasterError(t, res, "productCatalog.deleteFailed"),
				);
			}
		} catch (err: unknown) {
			toast.error(
				translateProductMasterError(t, err, "productCatalog.deleteFailed"),
			);
		} finally {
			setDeleting(false);
		}
	};

	if (!isOpen || !form) return null;

	const selectedImage = availableImages[selectedIndex];
	const missingRoles = IMAGE_ROLES.filter(
		(role) => !availableImages.some((img) => img.role === role),
	);

	const catValue = categories.find((c) => c.id === form.item_cat) || null;
	const famValue = families.find((f) => f.id === form.family) || null;
	const secValue = sectors.find((s) => s.id === form.sector) || null;

	return (
		<>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
				hidden
				onChange={handleFileChange}
			/>

			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="lg"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxHeight: "95vh" }}
			>
				<MoleDialogHeader
					eyebrow={
						isCreate
							? t("productCatalog.registerEyebrow", { defaultValue: "New product" })
							: t("productCatalog.itemDetailEyebrow", { defaultValue: "Product master" })
					}
					title={
						isCreate
							? form.item_name.trim() ||
								t("productCatalog.registerTitle", { defaultValue: "Register product" })
							: form.item_name || t("tables.item")
					}
					subtitle={
						isCreate
							? t("productCatalog.registerSubtitle", {
									defaultValue: "Fill in details, then save to unlock images",
								})
							: form.item_code
								? `${t("report.itemCode")}: ${form.item_code}`
								: t("productCatalog.itemDetailSubtitle", {
										defaultValue: "Images & product details",
									})
					}
					icon={<Inventory2 />}
					onClose={toggle}
					closeLabel={t("ui.buttons.close")}
					themeColor={themeColor}
				/>

				<DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
					<Grid container spacing={3}>
						{/* ── Gallery (cache-backed) ── */}
						<Grid size={{ xs: 12, md: 6 }}>
							<Paper
								elevation={0}
								sx={{
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 2,
									overflow: "hidden",
									position: "relative",
									height: { xs: 280, sm: 360 },
									bgcolor: "action.hover",
								}}
							>
								{isCreate || !itemId ? (
									<Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} p={2}>
										<ImageOutlined sx={{ fontSize: 56, color: "text.disabled" }} />
										<Typography color="text.secondary" textAlign="center" variant="body2">
											{t("productCatalog.saveBeforeImages", {
												defaultValue: "Save the product first to add images",
											})}
										</Typography>
									</Stack>
								) : probing || uploadingRole ? (
									<Stack height="100%" alignItems="center" justifyContent="center">
										<CircularProgress size={48} />
										{uploadingRole && (
											<Typography variant="caption" color="text.secondary" mt={1}>
												{t("productCatalog.uploading", { defaultValue: "Uploading…" })} ({uploadingRole})
											</Typography>
										)}
									</Stack>
								) : selectedImage ? (
									<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
										<Box
											component="img"
											src={selectedImage.url}
											alt={selectedImage.role}
											sx={{
												width: "100%",
												height: "100%",
												objectFit: "contain",
												p: 2,
											}}
										/>
										<Stack direction="row" spacing={1} sx={{ position: "absolute", top: 8, right: 8 }}>
											{canEditImages && <Tooltip title={t("productCatalog.replaceImage", { defaultValue: "Replace image" })}>
												<IconButton
													onClick={() => openFilePicker(selectedImage.role)}
													sx={{ bgcolor: "rgba(0,0,0,0.4)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.6)" } }}
												>
													<CloudUpload />
												</IconButton>
											</Tooltip>}
											<IconButton
												onClick={() => setFullscreenOpen(true)}
												sx={{ bgcolor: "rgba(0,0,0,0.4)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.6)" } }}
											>
												<Fullscreen />
											</IconButton>
										</Stack>
									</Box>
								) : (
									<Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} p={2}>
										<ImageOutlined sx={{ fontSize: 56, color: "text.disabled" }} />
										<Typography color="text.secondary" textAlign="center">
											{t("inventory.noImagesAvailable")}
										</Typography>
										{canEditImages && (
											<Button
												variant="contained"
												size="small"
												startIcon={<PhotoCamera />}
												onClick={() => openFilePicker("main")}
											>
												{t("productCatalog.uploadMain", { defaultValue: "Upload main image" })}
											</Button>
										)}
									</Stack>
								)}
							</Paper>

							{!isCreate && itemId > 0 && availableImages.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
									<Chip
										size="small"
										label={t("inventory.imageCount", {
											current: selectedIndex + 1,
											total: availableImages.length,
										})}
									/>
								</Box>
							)}

							{!isCreate && itemId > 0 && availableImages.length > 0 && (
								<ImageList cols={5} gap={8} sx={{ mt: 1, overflowX: "auto", pb: 0.5 }}>
									{availableImages.map(({ role, url }, index) => (
										<ImageListItem
											key={role}
											sx={{
												cursor: "pointer",
												border: selectedIndex === index ? "2px solid" : "2px solid transparent",
												borderColor: selectedIndex === index ? "primary.main" : "transparent",
												borderRadius: 1.5,
												overflow: "hidden",
												opacity: selectedIndex === index ? 1 : 0.8,
											}}
											onClick={() => setSelectedIndex(index)}
										>
											<img
												src={url}
												alt={role}
												loading="lazy"
												style={{ width: "100%", height: 72, objectFit: "cover" }}
											/>
											<ImageListItemBar
												position="bottom"
												sx={{
													background:
														"linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
													py: 0.25,
												}}
												title={
													<Typography variant="caption" sx={{ textTransform: "capitalize" }}>
														{t(`inventory.imageRole.${role}`, role.replace("-", " "))}
													</Typography>
												}
												actionIcon={
													canEditImages ? (
														<IconButton
															size="small"
															sx={{ color: "white" }}
															onClick={(ev) => {
																ev.stopPropagation();
																openFilePicker(role);
															}}
														>
															<CloudUpload fontSize="small" />
														</IconButton>
													) : undefined
												}
											/>
										</ImageListItem>
									))}
								</ImageList>
							)}

							{!isCreate && itemId > 0 && missingRoles.length > 0 && (
								<Box sx={{ mt: 1.5 }}>
									<Typography variant="overline" color="text.secondary" fontWeight={700}>
										{t("productCatalog.addViews", { defaultValue: "Add image views" })}
									</Typography>
									<Stack direction="row" flexWrap="wrap" gap={1} mt={0.75}>
										{missingRoles.map((role) => (
											<Chip
												key={role}
												icon={<PhotoCamera fontSize="small" />}
												label={t(`inventory.imageRole.${role}`, role.replace("-", " "))}
												onClick={() => openFilePicker(role)}
												variant="outlined"
												color="primary"
												sx={{ textTransform: "capitalize" }}
											/>
										))}
									</Stack>
								</Box>
							)}
						</Grid>

						{/* ── Editable master data ── */}
						<Grid size={{ xs: 12, md: 6 }}>
							<Stack spacing={2}>
								<Typography variant="overline" color="text.secondary" fontWeight={700}>
									{t("productCatalog.masterData", { defaultValue: "Product details" })}
								</Typography>

								<TextField
									size="small"
									label={t("tables.itemCode")}
									variant="filled"
									fullWidth
									value={form.item_code}
									onChange={(e) => patchForm("item_code", e.target.value)}
									inputProps={{ maxLength: 50 }}
								/>
								<TextField
									size="small"
									label={t("tables.item")}
									variant="filled"
									fullWidth
									required
									value={form.item_name}
									onChange={(e) => patchForm("item_name", e.target.value)}
									inputProps={{ maxLength: ITEM_NAME_MAX_LENGTH }}
								/>

								<Grid container spacing={1.5}>
									<Grid size={{ xs: 12, sm: 4 }}>
										<Autocomplete
											size="small"
											options={categories}
											loading={lookupsLoading}
											value={catValue}
											onChange={(_, v) => v && patchForm("item_cat", v.id)}
											getOptionLabel={(o) => o.label}
											isOptionEqualToValue={(a, b) => a.id === b.id}
											renderInput={(params) => (
												<TextField {...params} variant="filled" label={t("tables.category")} />
											)}
										/>
									</Grid>
									<Grid size={{ xs: 12, sm: 4 }}>
										<Autocomplete
											size="small"
											options={families}
											loading={lookupsLoading}
											value={famValue}
											onChange={(_, v) => v && patchForm("family", v.id)}
											getOptionLabel={(o) => o.label}
											isOptionEqualToValue={(a, b) => a.id === b.id}
											renderInput={(params) => (
												<TextField {...params} variant="filled" label={t("tables.family")} />
											)}
										/>
									</Grid>
									<Grid size={{ xs: 12, sm: 4 }}>
										<Autocomplete
											size="small"
											options={sectors}
											loading={lookupsLoading}
											value={secValue}
											onChange={(_, v) => v && patchForm("sector", v.id)}
											getOptionLabel={(o) => o.label}
											isOptionEqualToValue={(a, b) => a.id === b.id}
											renderInput={(params) => (
												<TextField {...params} variant="filled" label={t("tables.sector")} />
											)}
										/>
									</Grid>
								</Grid>

								<Grid container spacing={1.5}>
									{canSeeCost && (
										<Grid size={{ xs: 6, sm: 3 }}>
											<TextField
												size="small"
												type="number"
												label={t("tables.unitCost", { defaultValue: "Cost" })}
												variant="filled"
												fullWidth
												value={form.cost_price}
												onChange={(e) =>
													patchForm("cost_price", Math.max(0, roundMoney(Number(e.target.value) || 0)))
												}
												inputProps={{ min: 0, max: MONEY_LIMITS.max, step: 0.01, readOnly: !canSetCost }}
												disabled={!canSetCost}
											/>
										</Grid>
									)}
									<Grid size={{ xs: 6, sm: 3 }}>
										<TextField
											size="small"
											type="number"
											label={t("tables.sellingPrice")}
											variant="filled"
											fullWidth
											value={form.selling_price}
											onChange={(e) =>
												patchForm("selling_price", Math.max(0, roundMoney(Number(e.target.value) || 0)))
											}
											inputProps={{ min: 0, max: MONEY_LIMITS.max, step: 0.01 }}
										/>
									</Grid>
									<Grid size={{ xs: 6, sm: 3 }}>
										<TextField
											size="small"
											type="number"
											label={t("productCatalog.minSellPrice", { defaultValue: "Min sell price" })}
											variant="filled"
											fullWidth
											value={form.min_sell_price}
											onChange={(e) =>
												patchForm("min_sell_price", Math.max(0, roundMoney(Number(e.target.value) || 0)))
											}
											inputProps={{ min: 0, max: MONEY_LIMITS.max, step: 0.01 }}
										/>
									</Grid>
									<Grid size={{ xs: 6, sm: 3 }}>
										<TextField
											size="small"
											type="number"
											label={t("productCatalog.minOrderQty", { defaultValue: "Min order qty" })}
											variant="filled"
											fullWidth
											value={form.min_order_qty}
											onChange={(e) => {
												const parsed = parseQtyInput(e.target.value, form.is_fractional);
												patchForm("min_order_qty", Math.max(form.is_fractional ? 0.0001 : 1, parsed ?? 1));
											}}
											inputProps={{
												min: form.is_fractional ? 0.0001 : 1,
												max: QTY_LIMITS.max,
												step: qtyStepAttr(form.is_fractional),
											}}
										/>
									</Grid>
								</Grid>

								<Stack direction="row" flexWrap="wrap" gap={1}>
									<FormControlLabel
										control={
											<Checkbox
												checked={form.is_fractional}
												onChange={(e) => patchForm("is_fractional", e.target.checked)}
											/>
										}
										label={t("productCatalog.fractional", { defaultValue: "Fractional quantities" })}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={form.is_metered}
												onChange={(e) => patchForm("is_metered", e.target.checked)}
											/>
										}
										label={t("productCatalog.metered", { defaultValue: "Metered (stock tracked)" })}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={form.is_active}
												onChange={(e) => patchForm("is_active", e.target.checked)}
											/>
										}
										label={t("productCatalog.active", { defaultValue: "Active" })}
									/>
								</Stack>

								{/* Barcodes — addable / removable; saved with product master */}
								<Box>
									<Typography variant="overline" color="text.secondary" fontWeight={700}>
										{t("productCatalog.barcodes", { defaultValue: "Barcodes" })}
									</Typography>
									<Stack direction="row" spacing={1} mt={0.75} alignItems="flex-start">
										<TextField
											size="small"
											variant="filled"
											fullWidth
											label={t("productCatalog.addBarcode", { defaultValue: "Add barcode" })}
											value={barcodeDraft}
											onChange={(e) => setBarcodeDraft(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													addBarcode();
												}
											}}
											inputProps={{ maxLength: 50 }}
										/>
										<Button
											variant="outlined"
											startIcon={<Add />}
											onClick={addBarcode}
											disabled={!barcodeDraft.trim()}
											sx={{ flexShrink: 0, mt: 0.5 }}
										>
											{t("ui.buttons.add", { defaultValue: "Add" })}
										</Button>
									</Stack>
									{barcodes.length === 0 ? (
										<Typography variant="caption" color="text.secondary" display="block" mt={1}>
											{t("productCatalog.noBarcodes", {
												defaultValue: "No barcodes yet",
											})}
										</Typography>
									) : (
										<Stack direction="row" flexWrap="wrap" gap={1} mt={1.25}>
											{barcodes.map((code) => (
												<Chip
													key={code}
													label={code}
													onDelete={() => removeBarcode(code)}
													deleteIcon={<DeleteOutline />}
													variant="outlined"
													color="secondary"
												/>
											))}
										</Stack>
									)}
								</Box>

								{(activeItem?.Created || activeItem?.Updated) && (
									<Stack direction="row" flexWrap="wrap" gap={1}>
										{activeItem?.Created && (
											<Chip
												size="small"
												variant="outlined"
												color="default"
												label={`${t("tableView.detailPanel.created")}: ${Util.formatDate(activeItem.Created, "full")}`}
											/>
										)}
										{activeItem?.Updated && (
											<Chip
												size="small"
												variant="outlined"
												color="primary"
												label={`${t("tableView.lastUpdated")}: ${Util.formatDate(activeItem.Updated, "full")}`}
											/>
										)}
									</Stack>
								)}
							</Stack>
						</Grid>
					</Grid>
				</DialogContent>

				<MoleDialogActions>
					{!isCreate && itemId > 0 && canRemoveItem && (
						<Button
							variant="outlined"
							color="error"
							startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
							onClick={() => setConfirmDeleteOpen(true)}
							disabled={saving || deleting}
							sx={{ mr: "auto" }}
						>
							{t("ui.buttons.delete", { defaultValue: "Delete" })}
						</Button>
					)}
					{selectedImage && itemId > 0 && canEditImages && (
						<Button
							variant="outlined"
							startIcon={<CloudUpload />}
							onClick={() => openFilePicker(selectedImage.role)}
							disabled={Boolean(uploadingRole) || deleting}
						>
							{t("productCatalog.replaceImage", { defaultValue: "Replace image" })}
						</Button>
					)}
					<Button
						variant="contained"
						startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
						onClick={() => void handleSave()}
						disabled={
							saving ||
							deleting ||
							(!dirty && !isCreate) ||
							(isCreate && !form.item_name.trim())
						}
					>
						{isCreate
							? t("productCatalog.register", { defaultValue: "Register" })
							: t("ui.buttons.save", { defaultValue: "Save" })}
					</Button>
					<MoleCancelButton onClick={toggle} disabled={deleting}>
						{t("ui.buttons.close")}
					</MoleCancelButton>
				</MoleDialogActions>
			</MoleDialog>

			<ConfirmDialog
				isOpen={confirmDeleteOpen}
				setIsOpen={setConfirmDeleteOpen}
				title={t("productCatalog.deleteConfirmTitle", { defaultValue: "Delete product?" })}
				message={t("productCatalog.deleteConfirmMessage", {
					name: form.item_name || activeItem?.ItemName || "",
					defaultValue:
						"This permanently deletes “{{name}}” and its barcodes. Sales history will block deletion — deactivate instead if the product is in use.",
				})}
				confirmBtnColor="error"
				confirmBtnVariant="contained"
				confirmBtnText={t("ui.buttons.delete", { defaultValue: "Delete" })}
				confirmBtnIcon={<Delete />}
				onConfirm={() => {
					void handleDelete();
				}}
			/>

			<Dialog
				open={fullscreenOpen}
				onClose={() => setFullscreenOpen(false)}
				fullScreen
				PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.95)" } }}
			>
				<IconButton
					onClick={() => setFullscreenOpen(false)}
					sx={{ position: "absolute", top: 16, right: 16, color: "white", zIndex: 1 }}
				>
					<Close />
				</IconButton>
				{selectedImage && (
					<Box
						component="img"
						src={selectedImage.url}
						alt={selectedImage.role}
						sx={{ width: "100%", height: "100%", objectFit: "contain", p: 4 }}
					/>
				)}
			</Dialog>
		</>
	);
};

export default ItemDetailModal;
